import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Edit3 } from 'lucide-react'
import { quickReplyService } from '../services/quickReplyService'
import type { QuickReply } from '../types/quickReply.types'
import { ConfirmDeleteModal } from '../components/common/ConfirmDeleteModal'
import { QuickReplyFormModal } from '../components/common/QuickReplyFormModal'
import toast from 'react-hot-toast'

const QuickRepliesPage: React.FC = () => {
  const navigate = useNavigate()
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [formQuickReply, setFormQuickReply] = useState<QuickReply | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<QuickReply | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadQuickReplies = async () => {
    try {
      setIsLoading(true)
      const res = await quickReplyService.getQuickReplies()
      if (res.success && Array.isArray(res.data)) {
        setQuickReplies(res.data)
      } else {
        setQuickReplies([])
      }
    } catch {
      toast.error('Erro ao carregar mensagens rápidas')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadQuickReplies()
  }, [])

  const openCreateForm = () => {
    setFormQuickReply(null)
    setFormOpen(true)
  }

  const openEditForm = (qr: QuickReply) => {
    setFormQuickReply(qr)
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setFormQuickReply(null)
  }

  const handleFormSaved = (saved: QuickReply) => {
    if (formQuickReply == null) {
      setQuickReplies(prev => [saved, ...prev])
    } else {
      setQuickReplies(prev => prev.map(qr => (qr.id === saved.id ? saved : qr)))
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      setIsDeleting(true)
      const res = await quickReplyService.deleteQuickReply(deleteTarget.id)
      if (res.success) {
        setQuickReplies(prev => prev.filter(qr => qr.id !== deleteTarget.id))
        if (formQuickReply?.id === deleteTarget.id) {
          closeForm()
        }
        setDeleteTarget(null)
        toast.success('Mensagem rápida excluída')
      } else {
        toast.error(res.message || 'Erro ao excluir mensagem rápida')
      }
    } catch {
      toast.error('Erro ao excluir mensagem rápida')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
              aria-label="Voltar ao dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Mensagens rápidas
              </h1>
              <p className="text-gray-600">Agilize seus atendimentos com mensagens rápidas.</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={openCreateForm}
              className="btn btn-primary btn-md inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar mensagem rápida
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-sm font-medium text-gray-700 mb-3">Suas mensagens rápidas</h2>
          {isLoading ? (
            <p className="text-gray-500 text-center py-8">Carregando mensagens...</p>
          ) : quickReplies.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nenhuma mensagem rápida cadastrada. Clique em &quot;Adicionar mensagem rápida&quot; para criar.
            </p>
          ) : (
            <ul className="space-y-3">
              {quickReplies.map(qr => (
                <li
                  key={qr.id}
                  className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{qr.title}</p>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">
                      {qr.content}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      onClick={() => openEditForm(qr)}
                      title="Editar"
                    >
                      <Edit3 className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      onClick={() => setDeleteTarget(qr)}
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <QuickReplyFormModal
        isOpen={formOpen}
        onClose={closeForm}
        quickReply={formQuickReply}
        onSaved={handleFormSaved}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Excluir mensagem rápida"
        message={
          deleteTarget ? (
            <>
              Deseja realmente excluir <strong>"{deleteTarget.title}"</strong>?
              <span className="block mt-1 text-gray-500">Esta ação não pode ser desfeita.</span>
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Excluir"
        isLoading={isDeleting}
      />
    </div>
  )
}

export default QuickRepliesPage
